import { AppError } from "../api/problem";
import type { DashboardData, EntitlementFreshness, License } from "../api/contract";
import { getDashboard } from "../api/endpoints";

// Centralized per-org entitlement snapshot. Both the shell (topbar freshness/plan +
// mutation gating) and the Overview page read from here, so seat mutations are gated
// consistently against the SAME freshness value even on pages that don't fetch it.
class OrgStore {
  currentOrgId = $state<string | null>(null);
  dashboard = $state<DashboardData | null>(null);
  freshness = $state<EntitlementFreshness | null>(null);
  loading = $state(false);
  /** True while a background refetch runs over existing data (stale-while-revalidate). */
  revalidating = $state(false);
  error = $state<AppError | null>(null);

  #controller: AbortController | null = null;

  get license(): License | null {
    return this.dashboard?.license ?? null;
  }

  /** Degraded => read-only. When we don't yet know freshness we allow the attempt
   * (the server fails closed with 503 anyway). */
  get mutationsAllowed(): boolean {
    return this.freshness ? this.freshness.mutationsAllowed : true;
  }
  get degraded(): boolean {
    return this.freshness?.status === "degraded";
  }
  get stale(): boolean {
    return this.freshness?.status === "stale";
  }

  setOrg(orgId: string | null): void {
    if (orgId === this.currentOrgId) return;
    this.currentOrgId = orgId;
    this.dashboard = null;
    this.freshness = null;
    this.error = null;
    this.#abort();
  }

  async load(orgId: string): Promise<void> {
    this.#abort();
    const controller = new AbortController();
    this.#controller = controller;
    const background = this.dashboard !== null && this.currentOrgId === orgId;
    if (background) this.revalidating = true;
    else this.loading = true;
    this.error = null;
    try {
      const res = await getDashboard(orgId, controller.signal);
      if (controller.signal.aborted) return;
      this.currentOrgId = orgId;
      this.dashboard = res.data;
      this.freshness = res.meta.entitlementFreshness;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (error instanceof AppError) this.error = error;
      else throw error;
    } finally {
      if (this.#controller === controller) {
        this.loading = false;
        this.revalidating = false;
        this.#controller = null;
      }
    }
  }

  reload(): void {
    if (this.currentOrgId) void this.load(this.currentOrgId);
  }

  #abort(): void {
    this.#controller?.abort();
    this.#controller = null;
    this.loading = false;
    this.revalidating = false;
  }
}

export const org = new OrgStore();
